import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Tab, TabList, TabPanel, TabPanels, Tabs } from 'primeng/tabs';
import { SpinnerOverlayService } from '../../Components/spinner/spinner-overlay.service';
import { EIGHTH_TARIMA, HALF_TARIMA, QUARTER_TARIMA, TARIMA } from '../../core/config/purchasing-unity.constants';
import { MessageService } from '../../shared/message.service';
import { IImage } from '../../shared/model/product.model';
import { NewOrderModal } from '../new-order-modal/new-order-modal';
import { ProductDetailService } from './product-detail-service';
import { IColor, IItem, ProductDetail } from './product-detail.model';
import { ProductCartService, CartItem } from '../product-cart-component/product-cart-service';
import { AccountService } from '../../login/account.service';
import { ItemService } from '../../entities/item/item-list/item.service';

@Component({
  selector: 'app-product-detail-dialog',
  imports: [
    TabPanel,
    TabPanels,
    Tab,
    TabList,
    Tabs,
    ReactiveFormsModule,
    FormsModule,
    CommonModule
  ],
  templateUrl: './product-detail-dialog.html',
  styleUrl: './product-detail-dialog.css',
})
export class ProductDetailDialog implements OnInit {
  product: ProductDetail | null = null
  items: IItem[] | null = null
  images: IImage[] | null = null
  @Output() saved = new EventEmitter<any>();
  readOnly: boolean = false;


  selectedDimension: string | null = null
  selectedThickness: number | null = null
  selectedPurchasingUnit: string | null = null
  productQuantity: number = 0
  selectedProductFraction: number = 0
  selectedColor: IColor | null = null
  selectedTexture: string | null = null
  selectedCoating: string | null = null
  selectedDescription: string | null = null
  selectedItem: IItem | null = null
  totalPrice: number = 0.0

  purchasingUnityOptions: string[] = []
  thicknessOptions: number[] = []
  colorsOptions: IColor[] = []
  texturesOptions: string[] = []
  coatingOptions: string[] = []

  mainImageUrl: string | null = null;

  // Bloque de indisponibilidad cuando el servicio devuelve price = 0
  isUnavailableBlock: boolean = false;
  unavailableMessage: string = '';

  tarimaCantidad: number = 0;
  halfTarimaCantidad: number = 0;
  quarterTarimaCantidad: number = 0;
  minimoCantidad: number = 0;

  unitPrice: number = 0;

  tarimaCantidadUnidades: number = 0;
  halfTarimaCantidadUnidades: number = 0;
  quarterTarimaCantidadUnidades: number = 0;
  minimoCantidadUnidades: number = 0;

  tarimaPrecioTotal: number = 0;
  halfTarimaPrecioTotal: number = 0;
  quarterTarimaPrecioTotal: number = 0;
  minimoPrecioTotal: number = 0;

  showTarima: boolean = false;
  showHalfTarima: boolean = false;
  showQuarterTarima: boolean = false;
  showMinimo: boolean = false;

  platformLimits: { halfPlatformLimit?: number, quarterPlatformLimit?: number, eighthPlatformLimit?: number } = {};

  constructor(
    public dialogRef: MatDialogRef<NewOrderModal>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private productCartService: ProductCartService,
    private spinner: SpinnerOverlayService,
    private productDetailServie: ProductDetailService,
    private messageService: MessageService,
    private accountService: AccountService,
    private itemService: ItemService
  ) {
  }

  close(): void {
    this.dialogRef.close()
  }

  ngOnInit() {
    this.readOnly = this.data?.readOnly ?? false;
    this.loadPlatformLimitsFromStorage();
    if (this.data?.skipFindById && this.data?.preselectedItem) {
      this.initializeFromPreloadedItem(this.data.preselectedItem);
      return;
    }
    this.findById()
  }

  private initializeFromPreloadedItem(rawItem: any): void {
    const normalizedItem: IItem = {
      id: String(rawItem?.id ?? this.data?.cartItemId ?? ''),
      dimensions: rawItem?.dimensions,
      thickness: rawItem?.thickness != null ? Number(rawItem.thickness) : 0,
      color: rawItem?.color,
      texture: rawItem?.texture ?? '',
      faceOne: this.normalizeFaceFlag(rawItem?.faceOne),
      faceTwo: this.normalizeFaceFlag(rawItem?.faceTwo),
      description: rawItem?.description ?? '',
      platform: rawItem?.platform != null ? String(rawItem.platform) : null,
      halfPlatform: rawItem?.halfPlatform != null ? String(rawItem.halfPlatform) : null,
      quarterPlatform: rawItem?.quarterPlatform != null ? String(rawItem.quarterPlatform) : null,
      eighthPlatform: rawItem?.eighthPlatform != null ? String(rawItem.eighthPlatform) : null,
      price: rawItem?.price != null ? Number(rawItem.price) : 0,
      leadTime: rawItem?.leadTime != null ? Number(rawItem.leadTime) : undefined,
      active: rawItem?.active ?? true,
    };

    this.items = [normalizedItem];
    this.selectedItem = normalizedItem;

    const mainImage = rawItem?.productImage ?? '';
    this.images = mainImage ? [{ url: mainImage } as IImage] : [];
    this.setMainImage(mainImage);

    const productId = Number(this.data?.id ?? rawItem?.productId ?? 0);
    this.product = {
      product: {
        id: Number.isNaN(productId) ? 0 : productId,
        name: rawItem?.name ?? '',
        category: rawItem?.category ?? '',
        productType: rawItem?.productType ?? '',
        finish: rawItem?.finish ?? '',
        description: rawItem?.description ?? '',
        usesAndApplications: rawItem?.usesAndApplications ?? '',
        design: rawItem?.design ?? '',
        active: rawItem?.active ?? true,
        images: this.images ?? [],
        items: this.items,
      }
    };

    this.applyPreselected();
  }

  loadPlatformLimitsFromStorage(): void {
    const stored = localStorage.getItem('platformLimits');
    if (stored) {
      try {
        this.platformLimits = JSON.parse(stored);
      } catch (e) {
        console.error('Error parsing platform limits from localStorage:', e);
        this.platformLimits = {};
      }
    }
  }

  handleLoading(loading: boolean) {
    if (loading) this.spinner.show(); else this.spinner.hide()
  }

  findById() {
    this.handleLoading(true)

    const addressNumber = this.accountService.userIdentity()?.user.addressNumber;

    this.productDetailServie.findById(this.data.id, addressNumber).subscribe({
      next: (response) => {
        this.product = response.body ?? null
        this.items = response.body?.product.items ?? null
        this.images = response.body?.product.images ?? null
        this.setMainImage(this.images?.[0]?.url ?? '');
        this.handleLoading(false)

        this.applyPreselected();

      }, error: () => {
        this.handleLoading(false)
      }
    })
  }

  setMainImage(url: string) {
    this.mainImageUrl = url;
  }

  get getDimensionsOptions(): string[] {
    return Array.from(
      new Set(
        (this.items ?? [])
          .map(p => p.dimensions)
          .filter((d): d is string => d !== undefined)
      )
    );
  }

  selectDimension(dimension: string) {
    if (this.selectedDimension === dimension) {
      return;
    }

    this.selectedDimension = dimension;

    this.selectedThickness = null
    this.selectedColor = null
    this.selectedTexture = null
    this.selectedPurchasingUnit = null
    this.productQuantity = 0
    this.totalPrice = 0
    this.selectedCoating = null
    this.selectedDescription = null
    this.resetTarimaQuantities()
    this.data.cartItemId = null;

    this.thicknessOptions = []
    this.colorsOptions = []
    this.texturesOptions = []
    this.coatingOptions = []
    this.purchasingUnityOptions = []

    this.thicknessOptions = this.getThicknessOptionsByDimension()
  }

  getThicknessOptionsByDimension(): number[] {
    return Array.from(
      new Set(
        (this.items ?? [])
          .filter(p => p.dimensions === this.selectedDimension)
          .map(p => p.thickness)
          .filter((t): t is number => t != null)
      )
    ).sort((a, b) => a - b);
  }

  get descriptionOptions(): string[] {
    const pendingColorSelection = this.colorsOptions.length > 0 && !this.selectedColor;
    const pendingTextureSelection = this.texturesOptions.length > 0 && !this.selectedTexture;
    const pendingCoatingSelection = this.coatingOptions.length > 0 && !this.selectedCoating;

    if (pendingColorSelection || pendingTextureSelection || pendingCoatingSelection) {
      return [];
    }

    return this.getDescriptionOptionsByCurrentSelection();
  }

  selectThickness(thickness: number) {
    if (this.selectedThickness === thickness) {
      return;
    }

    this.selectedThickness = thickness;

    this.selectedColor = null
    this.selectedTexture = null
    this.selectedPurchasingUnit = null
    this.productQuantity = 0
    this.totalPrice = 0
    this.selectedCoating = null
    this.selectedDescription = null
    this.resetTarimaQuantities()
    this.data.cartItemId = null;

    this.colorsOptions = []
    this.texturesOptions = []
    this.coatingOptions = []
    this.purchasingUnityOptions = []

    this.colorsOptions = this.getColorsOptionsByDimensionAndThickness()

    if (this.colorsOptions.length === 0) {
      this.coatingOptions = this.coatingOptionsByDimensionAndThicknessAndColorAndTexture()
      if (this.coatingOptions.length === 0) {
        this.purchasingUnityOptions = this.getPurchasingUnityOptionsByDimensionAndThicknessAndColorAndTexture()
      }
    }
  }

  getColorsOptionsByDimensionAndThickness(): IColor[] {
    const colors =
      (this.items ?? [])
        .filter(p =>
          p.dimensions === this.selectedDimension &&
          p.thickness === this.selectedThickness &&
          p.color
        )
        .flatMap(p => p.color)
        .filter((c): c is IColor => c != null);

    const unique = Array.from(
      new Map(colors.map(c => [c.name, c])).values()
    );

    // Ordenar alfabéticamente por nombre
    unique.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));

    return unique;

  }

  selectColor(color: IColor) {
    if (this.selectedColor?.id === color.id) {
      return;
    }

    this.selectedColor = color;

    this.selectedTexture = null
    this.selectedPurchasingUnit = null
    this.productQuantity = 0
    this.totalPrice = 0
    this.selectedCoating = null
    this.selectedDescription = null
    this.resetTarimaQuantities()
    this.data.cartItemId = null;

    this.texturesOptions = []
    this.purchasingUnityOptions = []
    this.coatingOptions = []

    this.texturesOptions = this.getTexturesOptionsByDimensionAndThicknessAndColor()
    if (this.texturesOptions.length === 0) {
      this.coatingOptions = this.coatingOptionsByDimensionAndThicknessAndColorAndTexture()
      if (this.coatingOptions.length === 0) {
        this.purchasingUnityOptions = this.getPurchasingUnityOptionsByDimensionAndThicknessAndColorAndTexture()
      }
    }
  }

  getTexturesOptionsByDimensionAndThicknessAndColor(): string[] {
    return Array.from(
      new Set(
        (this.items ?? [])
          .map(p => ({ ...p, texture: (p.texture ?? '').trim() }))
          .filter(p =>
            p.dimensions === this.selectedDimension &&
            p.thickness === this.selectedThickness &&
            p.texture !== '' &&
            p.texture !== 'NA' &&
            p.color?.id === this.selectedColor?.id
          )
          .map(p => p.texture!)
      )
    ).sort((a, b) => a.localeCompare(b));
  }

  selectTexture(texture: string) {
    if (this.selectedTexture === texture) {
      return;
    }

    this.selectedTexture = texture;

    this.selectedCoating = null
    this.selectedPurchasingUnit = null
    this.productQuantity = 0
    this.totalPrice = 0
    this.selectedDescription = null
    this.resetTarimaQuantities()
    this.data.cartItemId = null;

    this.coatingOptions = []
    this.purchasingUnityOptions = []

    this.coatingOptions = this.coatingOptionsByDimensionAndThicknessAndColorAndTexture()

    if (this.coatingOptions.length === 0) {
      this.purchasingUnityOptions = this.getPurchasingUnityOptionsByDimensionAndThicknessAndColorAndTexture()
    }
  }

  coatingOptionsByDimensionAndThicknessAndColorAndTexture(): string[] {
    const coatingOptions: Set<string> = new Set();

    // Buscar todos los items que coincidan con los filtros actuales
    const matchingItems = this.items?.filter(p => this.matchesFilters(p)) ?? [];

    // Extraer qué combinaciones de caras existen realmente
    matchingItems.forEach(item => {
      const { faceOne, faceTwo } = this.getItemFaces(item);
      const coating = this.determineCoatingFromFaces(faceOne, faceTwo);
      if (coating) coatingOptions.add(coating);
    });

    return Array.from(coatingOptions);
  }

  selectCoating(coating: string) {
    this.selectedCoating = coating;
    this.selectedDescription = null;

    this.selectedPurchasingUnit = null
    this.productQuantity = 0
    this.totalPrice = 0
    this.resetTarimaQuantities()
    this.data.cartItemId = null;

    this.updateSelectedItemByCoatingStrict();
  }

  selectDescription(description: string) {
    if (this.selectedDescription === description) {
      return;
    }

    this.selectedDescription = description;

    this.selectedPurchasingUnit = null
    this.productQuantity = 0
    this.totalPrice = 0
    this.resetTarimaQuantities()
    this.data.cartItemId = null;

    this.updateSelectedItemByCoatingStrict();
  }

  private updateSelectedItemByCoatingStrict(): void {
    if (!this.selectedDimension || !this.selectedThickness) return;
    
    if (this.coatingOptions && this.coatingOptions.length > 0 && !this.selectedCoating) return;

    if (!this.ensureDescriptionSelection()) {
      return;
    }

    const item = this.findItemByFilters(true);

    if (item) {
      const oldItemId = this.selectedItem?.id;
      const newItemId = item.id;
      if (oldItemId !== newItemId) this.data.cartItemId = null;

      this.selectedItem = item;

      // Obtener precio actualizado antes de mostrar plataformas
      const customerAN = this.accountService.userIdentity()?.user.addressNumber;
      if (customerAN && item.id) {
        this.handleLoading(true);
        this.productCartService.getPriceJDE([item.id.toString()], customerAN.toString()).subscribe({
          next: (resp: any) => {
            const found = resp?.priceItems?.find((p: any) => p.itemId === item.id.toString());
            if (found && found.price != null && found.price > 0) {
              this.selectedItem!.price = found.price;
              this.unitPrice = found.price;
              this.isUnavailableBlock = false;
              this.unavailableMessage = '';
              this.updateTarimaOptionsForSelectedItem();
              this.checkAndLoadExistingCartItem();
              this.handleLoading(false);
            } else {
              this.handleLoading(false);
              this.selectedItem!.price = 0;
              this.unitPrice = 0;
              this.purchasingUnityOptions = [];
              this.isUnavailableBlock = true;
              this.unavailableMessage = 'el precio del item no esta disponible';
            }
          },
          error: (err) => {
            this.handleLoading(false);
            this.selectedItem!.price = 0;
            this.unitPrice = 0;
            this.purchasingUnityOptions = [];
            this.isUnavailableBlock = false;
            this.unavailableMessage = '';
            const msg = this.getServiceErrorMessage(err);
            this.messageService.showError(msg);
          }
        });
      } else {
        this.updateTarimaOptionsForSelectedItem();
        this.checkAndLoadExistingCartItem();
      }
    } else {
      this.selectedItem = null;
      this.purchasingUnityOptions = [];
      this.isUnavailableBlock = false;
      this.unavailableMessage = '';
    }
  }

  private checkAndLoadExistingCartItem() {
    if (!this.selectedItem) {
      return;
    }

    const cart = this.productCartService.getCart();

    const existingItem = cart.find(item => item.id === this.selectedItem!.id);

    if (existingItem) {
      this.tarimaCantidad = existingItem.platformCount ?? 0;
      this.halfTarimaCantidad = existingItem.halfPlatformCount ?? 0;
      this.quarterTarimaCantidad = existingItem.quarterPlatformCount ?? 0;
      this.minimoCantidad = existingItem.eighthPlatformCount ?? 0;
      this.updateTotalPrice();

      this.data.cartItemId = existingItem.id;
      this.messageService.showSuccess('Se cargaron las cantidades del item existente en tu carrito.');
    } else {
      this.data.cartItemId = null;
    }
  }

  getPurchasingUnityOptionsByDimensionAndThicknessAndColorAndTexture(): string[] {
    if (!this.ensureDescriptionSelection()) {
      return [];
    }

    const item = this.findItemByFilters(true);

    if (!item) {
      console.error('No se encontró el producto con las opciones seleccionadas.');
      return [];
    }

    this.selectedItem = item;
    const customerAN = this.accountService.userIdentity()?.user.addressNumber;
    if (customerAN && item.id) {
      this.handleLoading(true);
      this.productCartService.getPriceJDE([item.id.toString()], customerAN.toString()).subscribe({
        next: (resp: any) => {
          const found = resp?.priceItems?.find((p: any) => p.itemId === item.id.toString());
          if (found && found.price != null && found.price > 0) {
            this.selectedItem!.price = found.price;
            this.unitPrice = found.price;
            this.isUnavailableBlock = false;
            this.unavailableMessage = '';
            this.configureTarimaOptions(item);
            this.resetTarimaQuantities();
            this.checkAndLoadExistingCartItem();
            this.handleLoading(false);
          } else {
            this.handleLoading(false);
            this.selectedItem!.price = 0;
            this.unitPrice = 0;
            this.purchasingUnityOptions = [];
            this.isUnavailableBlock = true;
            this.unavailableMessage = 'el precio del item no esta disponible';
          }
        },
        error: (err) => {
          this.handleLoading(false);
          this.selectedItem!.price = 0;
          this.unitPrice = 0;
          this.purchasingUnityOptions = [];
          this.isUnavailableBlock = false;
          this.unavailableMessage = '';
          const msg = this.getServiceErrorMessage(err);
          this.messageService.showError(msg);
        }
      });
    } else {
      this.configureTarimaOptions(item);
      this.resetTarimaQuantities();
    }
    return this.purchasingUnityOptions;
  }

  resetTarimaQuantities(): void {
    this.tarimaCantidad = 0;
    this.halfTarimaCantidad = 0;
    this.quarterTarimaCantidad = 0;
    this.minimoCantidad = 0;
    this.updateTotalPrice();
  }

  incrementTarima(): void {
    this.tarimaCantidad++;
    this.updateTotalPrice();
  }

  decrementTarima(): void {
    if (this.tarimaCantidad > 0) {
      this.tarimaCantidad--;
      this.updateTotalPrice();
    }
  }

  incrementHalfTarima(): void {
    const limit = this.platformLimits.halfPlatformLimit ?? Infinity;
    if (this.halfTarimaCantidad < limit) {
      this.halfTarimaCantidad++;
      this.updateTotalPrice();
    } else {
      this.messageService.showError(`Se alcanzó el límite máximo de media plataforma: ${limit}`);
    }
  }

  decrementHalfTarima(): void {
    if (this.halfTarimaCantidad > 0) {
      this.halfTarimaCantidad--;
      this.updateTotalPrice();
    }
  }

  incrementQuarterTarima(): void {
    const limit = this.platformLimits.quarterPlatformLimit ?? Infinity;
    if (this.quarterTarimaCantidad < limit) {
      this.quarterTarimaCantidad++;
      this.updateTotalPrice();
    } else {
      this.messageService.showError(`Se alcanzó el límite máximo de cuarto de plataforma: ${limit}`);
    }
  }

  decrementQuarterTarima(): void {
    if (this.quarterTarimaCantidad > 0) {
      this.quarterTarimaCantidad--;
      this.updateTotalPrice();
    }
  }

  incrementMinimo(): void {
    const limit = this.platformLimits.eighthPlatformLimit ?? Infinity;
    if (this.minimoCantidad < limit) {
      this.minimoCantidad++;
      this.updateTotalPrice();
    } else {
      this.messageService.showError(`Se alcanzó el límite máximo de octavo de plataforma: ${limit}`);
    }
  }

  decrementMinimo(): void {
    if (this.minimoCantidad > 0) {
      this.minimoCantidad--;
      this.updateTotalPrice();
    }
  }

  updateTotalPrice(): void {
    this.tarimaPrecioTotal = this.tarimaCantidad * this.tarimaCantidadUnidades * this.unitPrice;
    this.halfTarimaPrecioTotal = this.halfTarimaCantidad * this.halfTarimaCantidadUnidades * this.unitPrice;
    this.quarterTarimaPrecioTotal = this.quarterTarimaCantidad * this.quarterTarimaCantidadUnidades * this.unitPrice;
    this.minimoPrecioTotal = this.minimoCantidad * this.minimoCantidadUnidades * this.unitPrice;

    this.totalPrice = Math.round((this.tarimaPrecioTotal + this.halfTarimaPrecioTotal + this.quarterTarimaPrecioTotal + this.minimoPrecioTotal) * 100) / 100;
  }

  getTotalUnidades(): number {
    const totalUnidades =
      (this.tarimaCantidad * this.tarimaCantidadUnidades) +
      (this.halfTarimaCantidad * this.halfTarimaCantidadUnidades) +
      (this.quarterTarimaCantidad * this.quarterTarimaCantidadUnidades) +
      (this.minimoCantidad * this.minimoCantidadUnidades);
    return totalUnidades;
  }


  get buttonDisable(): boolean {
    return this.getTotalUnidades() === 0 || this.isUnavailableBlock
  }

  addItemToCart() {
    if (!this.selectedItem) {
      this.messageService.showError("Error: No se ha seleccionado ningún ítem para agregar al carrito.");
      return;
    }

    if (this.isUnavailableBlock) {
      return;
    }

    const totalUnidades = this.getTotalUnidades();
    if (totalUnidades === 0) {
      this.messageService.showError("Error: Debe seleccionar al menos una unidad.");
      return;
    }

    if (this.selectedItem.leadTime != null) {
      this.buildAndSaveCartItem(this.selectedItem.leadTime, totalUnidades);
      return;
    }

    this.handleLoading(true);
    const addressNumber = this.accountService.userIdentity()?.user.addressNumber;
    this.itemService.find_by_ids([this.selectedItem.id], addressNumber).subscribe({
      next: (response) => {
        this.handleLoading(false);
        const items = response.body?.item?.itemList ?? [];
        const apiItem = items.find((i: any) => i.id === this.selectedItem!.id);
        this.buildAndSaveCartItem(apiItem?.leadTime ?? undefined, totalUnidades);
      },
      error: () => {
        this.handleLoading(false);
        this.buildAndSaveCartItem(undefined, totalUnidades);
      }
    });
  }

  private buildAndSaveCartItem(leadTime: number | undefined, totalUnidades: number) {
    const cartItem = {
      id: this.selectedItem!.id,
      productId: this.product?.product.id!!,
      productImage: this.product?.product.images[0]?.url ?? '',
      name: this.product?.product.name!!,
      description: this.selectedItem!.description,
      dimensions: this.selectedItem!.dimensions!!,
      faceOne: this.selectedCoating === '1 Cara',
      faceTwo: this.selectedCoating === '2 Caras',
      thickness: this.selectedItem!.thickness,
      color: this.selectedItem!.color,
      texture: this.selectedItem!.texture,
      price: this.selectedItem!.price!!,
      leadTime: leadTime,
      quantity: totalUnidades,
      platformCount: this.tarimaCantidad,
      halfPlatformCount: this.halfTarimaCantidad,
      quarterPlatformCount: this.quarterTarimaCantidad,
      eighthPlatformCount: this.minimoCantidad
    };

    if (this.data?.cartItemId) {
      cartItem.id = this.data.cartItemId;
      this.productCartService.updateItem(cartItem);
      this.messageService.showSuccess("El ítem ha sido actualizado correctamente.");
      this.saved.emit({ action: 'save', item: cartItem, cartItemId: this.data.cartItemId, isUpdate: true, handledByDialog: true, updated: cartItem });
      this.dialogRef.close({ action: 'save', item: cartItem, updated: true });
      return;
    }

    this.productCartService.addItem(cartItem);

    this.messageService.showSuccess("El ítem ha sido agregado al carrito de compras correctamente.");

    this.saved.emit({ action: 'save', item: cartItem });
    this.dialogRef.close({ action: 'save', item: cartItem });
  }

  private updateTarimaOptionsForSelectedItem(): void {
    if (!this.selectedItem) return;
    const customerAN = this.accountService.userIdentity()?.user.addressNumber;
    if (customerAN && this.selectedItem.id) {
      this.handleLoading(true);
      this.productCartService.getPriceJDE([this.selectedItem.id.toString()], customerAN.toString()).subscribe({
        next: (resp: any) => {
          const found = resp?.priceItems?.find((p: any) => p.itemId === this.selectedItem!.id.toString());
          if (found && found.price != null && found.price > 0) {
            this.selectedItem!.price = found.price;
            this.unitPrice = found.price;
            this.isUnavailableBlock = false;
            this.unavailableMessage = '';
            this.configureTarimaOptions(this.selectedItem!);
            this.handleLoading(false);
          } else {
            this.handleLoading(false);
            this.selectedItem!.price = 0;
            this.unitPrice = 0;
            this.purchasingUnityOptions = [];
            this.isUnavailableBlock = true;
            this.unavailableMessage = 'el precio del item no esta disponible';
          }
        },
        error: (err) => {
          this.handleLoading(false);
          this.selectedItem!.price = 0;
          this.unitPrice = 0;
          this.purchasingUnityOptions = [];
          this.isUnavailableBlock = false;
          this.unavailableMessage = '';
          const msg = this.getServiceErrorMessage(err);
          this.messageService.showError(msg);
        }
      });
    } else {
      this.configureTarimaOptions(this.selectedItem!);
    }
  }

  private normalizeFaceFlag(flag: any): boolean {
    if (flag === true || flag === false) return flag;
    if (typeof flag === 'string') {
      const v = flag.trim().toLowerCase();
      if (v === 'true' || v === '1' || v === 'yes' || v === 'y') return true;
      if (v === 'false' || v === '0' || v === 'no' || v === 'n') return false;
    }
    if (typeof flag === 'number') {
      return flag !== 0;
    }
    return !!flag;
  }

  // Extrae mensaje de error del payload devuelto por el backend.
  // Maneja formatos: { reason }, { message }, y envoltorios donde body es un JSON string.
  private getServiceErrorMessage(err: any): string {
    if (!err) return 'No se pudo obtener el precio del producto en este momento.';

    // err.error puede ser un objeto parseado o un string
    const body = err.error;

    if (body) {
      // Si body tiene reason o message directo
      if (body.reason) return String(body.reason);
      if (body.message) return String(body.message);

      // Algunos gateways envuelven el payload en `body` como string
      if (body.body) {
        try {
          const parsed = typeof body.body === 'string' ? JSON.parse(body.body) : body.body;
          if (parsed) {
            if (parsed.reason) return String(parsed.reason);
            if (parsed.message) return String(parsed.message);
          }
        } catch (e) {
          // ignore
        }
      }
    }

    // Fallbacks
    if (err.message) return String(err.message);
    return 'No se pudo obtener el precio del producto en este momento.';
  }

  private getItemFaces(item: IItem): { faceOne: boolean; faceTwo: boolean } {
    return {
      faceOne: this.normalizeFaceFlag(item.faceOne),
      faceTwo: this.normalizeFaceFlag(item.faceTwo)
    };
  }

  private determineCoatingFromFaces(faceOne: boolean, faceTwo: boolean): string | null {
    if (faceOne && !faceTwo) return '1 Cara';
    if (!faceOne && faceTwo) return '2 Caras';
    return null;
  }

  private matchesCoating(faceOne: boolean, faceTwo: boolean, coating: string | null): boolean {
    if (!coating) return true;
    if (coating === '1 Cara') return faceOne === true && faceTwo === false;
    if (coating === '2 Caras') return faceOne === false && faceTwo === true;
    return false;
  }

  private matchesFilters(product: IItem): boolean {
    if (product.dimensions !== this.selectedDimension || product.thickness !== this.selectedThickness) return false;
    if (this.selectedColor && product.color?.id !== this.selectedColor?.id) return false;
    if (this.selectedTexture) {
      const productTexture = (product.texture ?? '').trim();
      if (productTexture !== this.selectedTexture) return false;
    }
    if (this.selectedDescription && (product.description ?? '').trim() !== this.selectedDescription) return false;
    return true;
  }

  private matchesFiltersWithoutDescription(product: IItem): boolean {
    if (product.dimensions !== this.selectedDimension || product.thickness !== this.selectedThickness) return false;
    if (this.selectedColor && product.color?.id !== this.selectedColor?.id) return false;
    if (this.selectedTexture) {
      const productTexture = (product.texture ?? '').trim();
      if (productTexture !== this.selectedTexture) return false;
    }
    if (this.selectedCoating) {
      const { faceOne, faceTwo } = this.getItemFaces(product);
      if (!this.matchesCoating(faceOne, faceTwo, this.selectedCoating)) return false;
    }
    return true;
  }

  private getDescriptionOptionsByCurrentSelection(): string[] {
    const descriptions = (this.items ?? [])
      .filter(product => this.matchesFiltersWithoutDescription(product))
      .map(product => (product.description ?? '').trim())
      .filter((description): description is string => description !== '');

    return Array.from(new Set(descriptions)).sort((a, b) => a.localeCompare(b));
  }

  private ensureDescriptionSelection(): boolean {
    const options = this.getDescriptionOptionsByCurrentSelection();

    if (options.length === 0) {
      this.selectedItem = null;
      this.purchasingUnityOptions = [];
      this.unitPrice = 0;
      this.isUnavailableBlock = false;
      this.unavailableMessage = '';
      return false;
    }

    if (!this.selectedDescription && options.length === 1) {
      this.selectedDescription = options[0];
    }

    if (!this.selectedDescription) {
      this.selectedItem = null;
      this.purchasingUnityOptions = [];
      this.unitPrice = 0;
      this.isUnavailableBlock = false;
      this.unavailableMessage = '';
      return false;
    }

    if (!options.includes(this.selectedDescription)) {
      this.selectedItem = null;
      this.purchasingUnityOptions = [];
      this.unitPrice = 0;
      this.isUnavailableBlock = false;
      this.unavailableMessage = '';
      return false;
    }

    return true;
  }

  private findItemByFilters(includeCoating: boolean = true): IItem | null {
    return this.items?.find(product => {
      if (!this.matchesFilters(product)) return false;
      if (!includeCoating) return true;
      const { faceOne, faceTwo } = this.getItemFaces(product);
      return this.matchesCoating(faceOne, faceTwo, this.selectedCoating);
    }) ?? null;
  }

  private resetOptions(): void {
    this.texturesOptions = [];
    this.coatingOptions = [];
    this.purchasingUnityOptions = [];
  }

  private configureTarimaOptions(item: IItem): void {
    const unitPrice = item.price ?? 0;

    this.showTarima = !!(item.platform && Number(item.platform));
    this.showHalfTarima = !!(item.halfPlatform && Number(item.halfPlatform));
    this.showQuarterTarima = !!(item.quarterPlatform && Number(item.quarterPlatform));
    this.showMinimo = !!(item.eighthPlatform && Number(item.eighthPlatform));

    this.tarimaCantidadUnidades = this.showTarima ? Number(item.platform) : 0;
    this.halfTarimaCantidadUnidades = this.showHalfTarima ? Number(item.halfPlatform) : 0;
    this.quarterTarimaCantidadUnidades = this.showQuarterTarima ? Number(item.quarterPlatform) : 0;
    this.minimoCantidadUnidades = this.showMinimo ? Number(item.eighthPlatform) : 0;

    this.unitPrice = unitPrice;

    const availableTarimaOption: string[] = [];
    if (this.showTarima) availableTarimaOption.push(TARIMA);
    if (this.showHalfTarima) availableTarimaOption.push(HALF_TARIMA);
    if (this.showQuarterTarima) availableTarimaOption.push(QUARTER_TARIMA);
    if (this.showMinimo) availableTarimaOption.push(EIGHTH_TARIMA);

    this.purchasingUnityOptions = availableTarimaOption;
  }

  private applyPreselected() {
    const p = this.data?.preselected;
    if (!p || !this.items) return;

    // Establecer dimensión y obtener espesores disponibles
    if (p.dimensions) {
      this.selectedDimension = p.dimensions;
      this.thicknessOptions = this.getThicknessOptionsByDimension();
    }

    // Establecer espesor y obtener colores disponibles
    if (p.thickness != null) {
      this.selectedThickness = p.thickness;
      this.colorsOptions = this.getColorsOptionsByDimensionAndThickness();
    }

    // Establecer color
    if (p.color) {
      const colorName = typeof p.color === 'string' ? p.color : (p.color.name ?? null);
      if (colorName) {
        if (!this.colorsOptions || this.colorsOptions.length === 0) {
          this.colorsOptions = this.getColorsOptionsByDimensionAndThickness();
        }
        this.selectedColor = this.colorsOptions.find(c => c.name === colorName) ?? null;
      }
    }

    // Establecer texture
    if (p.texture) {
      this.selectedTexture = p.texture;
    }
    this.texturesOptions = this.getTexturesOptionsByDimensionAndThicknessAndColor();

    if (p.description) {
      this.selectedDescription = String(p.description).trim();
    }

    if (!this.selectedDescription && this.selectedItem?.description) {
      this.selectedDescription = this.selectedItem.description;
    }

    // Establecer coating (prioridad: faceOne/faceTwo > coating)
    if (p.faceOne !== undefined || p.faceTwo !== undefined) {
      const faceOne = this.normalizeFaceFlag(p.faceOne);
      const faceTwo = this.normalizeFaceFlag(p.faceTwo);
      this.selectedCoating = this.determineCoatingFromFaces(faceOne, faceTwo);
    } else if (p.coating) {
      this.selectedCoating = p.coating;
    }

    // Calcular opciones de coating disponibles
    this.coatingOptions = this.coatingOptionsByDimensionAndThicknessAndColorAndTexture();

    // Buscar el item con el coating establecido
    this.selectedItem = this.findItemByFilters(true);

    // Si no se encontró con coating y hay uno especificado, intentar sin él
    if (!this.selectedItem && this.selectedCoating) {
      this.selectedCoating = null;
      this.selectedItem = this.findItemByFilters(false);
    }

    if (this.selectedItem && !this.selectedDescription) {
      this.selectedDescription = this.selectedItem.description;
    }

    // Cargar cantidades desde preseleccionados
    const platformCount = p.platformCount ?? (p as any).platform_count ?? (p as any).tarimaCantidad ?? (p as any).tarima_cantidad;
    const halfPlatformCount = p.halfPlatformCount ?? (p as any).half_platform_count ?? (p as any).halfTarimaCantidad ?? (p as any).half_tarima_cantidad;
    const quarterPlatformCount = p.quarterPlatformCount ?? (p as any).quarter_platform_count ?? (p as any).quarterTarimaCantidad ?? (p as any).quarter_tarima_cantidad;
    const eighthPlatformCount = p.eighthPlatformCount ?? (p as any).eighth_platform_count ?? (p as any).minimoCantidad ?? (p as any).minimo_cantidad;

    this.tarimaCantidad = Number(platformCount ?? 0) || 0;
    this.halfTarimaCantidad = Number(halfPlatformCount ?? 0) || 0;
    this.quarterTarimaCantidad = Number(quarterPlatformCount ?? 0) || 0;
    this.minimoCantidad = Number(eighthPlatformCount ?? 0) || 0;
    this.productQuantity = this.getTotalUnidades();

    // Configurar todo basado en el item seleccionado
    if (this.selectedItem) {
      const { faceOne, faceTwo } = this.getItemFaces(this.selectedItem);
      this.selectedCoating = this.determineCoatingFromFaces(faceOne, faceTwo);
      this.coatingOptions = [this.selectedCoating].filter((c): c is string => c !== null);

      const customerAN = this.accountService.userIdentity()?.user.addressNumber;
      if (customerAN && this.selectedItem?.id) {
        this.handleLoading(true);
        this.productCartService.getPriceJDE([this.selectedItem.id.toString()], customerAN.toString()).subscribe({
          next: (resp: any) => {
            const found = resp?.priceItems?.find((p: any) => p.itemId === this.selectedItem!.id.toString());
            if (found && found.price != null && found.price > 0) {
              this.selectedItem!.price = found.price;
              this.unitPrice = found.price;
              this.isUnavailableBlock = false;
              this.unavailableMessage = '';
              this.configureTarimaOptions(this.selectedItem!);
              this.checkAndLoadExistingCartItem();
              this.handleLoading(false);
            } else {
              this.handleLoading(false);
                this.selectedItem!.price = 0;
                this.unitPrice = 0;
                this.purchasingUnityOptions = [];
                this.isUnavailableBlock = true;
                this.unavailableMessage = 'el precio del item no esta disponible';
            }
          },
          error: (err) => {
            this.handleLoading(false);
            this.selectedItem!.price = 0;
            this.unitPrice = 0;
            this.purchasingUnityOptions = [];
            const msg = this.getServiceErrorMessage(err);
            this.messageService.showError(msg);
          }
        });
      } else {
        this.configureTarimaOptions(this.selectedItem!);
      }

      if (this.selectedTexture && !this.texturesOptions.includes(this.selectedTexture)) {
        this.texturesOptions = Array.from(new Set([...this.texturesOptions, this.selectedTexture]));
      }

      const q = Number(this.productQuantity);
      if (this.selectedItem.platform && Number(this.selectedItem.platform) === q) this.selectedPurchasingUnit = TARIMA;
      else if (this.selectedItem.halfPlatform && Number(this.selectedItem.halfPlatform) === q) this.selectedPurchasingUnit = HALF_TARIMA;
      else if (this.selectedItem.quarterPlatform && Number(this.selectedItem.quarterPlatform) === q) this.selectedPurchasingUnit = QUARTER_TARIMA;
      else if (this.selectedItem.eighthPlatform && Number(this.selectedItem.eighthPlatform) === q) this.selectedPurchasingUnit = EIGHTH_TARIMA;
    }

    this.updateTotalPrice();
  }

  validatePositiveInteger(field: string, value: any): void {
    const numValue = Number(value);
    
    if (Number.isNaN(numValue) || numValue < 0 || !Number.isInteger(numValue)) {
      switch(field) {
        case 'tarimaCantidad':
          this.tarimaCantidad = 0;
          break;
        case 'halfTarimaCantidad':
          this.halfTarimaCantidad = 0;
          break;
        case 'quarterTarimaCantidad':
          this.quarterTarimaCantidad = 0;
          break;
        case 'minimoCantidad':
          this.minimoCantidad = 0;
          break;
      }
    }
    
    this.updateTotalPrice();
  }

  onlyDigits(event: KeyboardEvent): void {
    const key = event.key;
    if (
      key === 'Backspace' ||
      key === 'Delete' ||
      key === 'Tab' ||
      key === 'Escape' ||
      key === 'Enter' ||
      key === 'ArrowLeft' ||
      key === 'ArrowRight' ||
      key === 'ArrowUp' ||
      key === 'ArrowDown' ||
      key === 'Home' ||
      key === 'End'
    ) {
      return;
    }
    if ((event.ctrlKey || event.metaKey) && (key === 'a' || key === 'c' || key === 'v' || key === 'x')) {
      return;
    }
    if (!/^\d$/.test(key)) {
      event.preventDefault();
    }
  }

  onPaste(event: ClipboardEvent): void {
    const pastedText = event.clipboardData?.getData('text');
    if (pastedText && !/^\d+$/.test(pastedText)) {
      event.preventDefault();
    }
  }

}
